import React, { useCallback, useEffect, useMemo, useState } from "react";
import companyApiService from "../../services/companyApiService";
import "./Analysis.scss";

type PeriodKey = "thisMonth" | "last7Days" | "last30Days" | "custom";

interface ShopOption {
  id: number;
  name: string;
}

interface MetricPayload {
  totalSales: number;
  returnedAmount: number;
  returnedItems: number;
  purchasingTotal: number;
  totalRevenue: number;
  cashSales: number;
  cardSales: number;
  bankSales: number;
  creditSales: number;
  creditRecovered: number;
  totalTransactions: number;
  totalCustomers: number;
  totalEmployees: number;
  tableSales: number;
  totalStockSold: number;
}

interface PaymentMixItem {
  label: string;
  value: number;
}

interface EmployeeSalesItem {
  employeeId: number;
  employeeName: string;
  totalSales: number;
  transactions: number;
}

interface CustomerSalesItem {
  customerId: number;
  customerName: string;
  phoneNumber: string;
  totalSales: number;
  transactions: number;
}

interface ShopSummaryItem {
  companyId: number;
  companyName: string;
  totalSales: number;
  cashSales: number;
  cardSales: number;
  bankSales: number;
  creditSales: number;
  transactions: number;
}

interface AnalysisResponse {
  period: {
    startDate: string;
    endDate: string;
  };
  selectedCompanyId: number | null;
  metrics: MetricPayload;
  paymentMix: PaymentMixItem[];
  employeeSales: EmployeeSalesItem[];
  customerSales: CustomerSalesItem[];
  shopSummary: ShopSummaryItem[];
  shopOptions: ShopOption[];
}

const emptyMetrics: MetricPayload = {
  totalSales: 0,
  returnedAmount: 0,
  returnedItems: 0,
  purchasingTotal: 0,
  totalRevenue: 0,
  cashSales: 0,
  cardSales: 0,
  bankSales: 0,
  creditSales: 0,
  creditRecovered: 0,
  totalTransactions: 0,
  totalCustomers: 0,
  totalEmployees: 0,
  tableSales: 0,
  totalStockSold: 0,
};

const formatQuantity = (value: number | null | undefined) =>
  Number(value || 0).toLocaleString("en-US");

const getCurrentMonthRange = (): { startDate: string; endDate: string } => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    startDate: start.toISOString().split("T")[0],
    endDate: today.toISOString().split("T")[0],
  };
};

const getRelativeRange = (
  days: number,
): { startDate: string; endDate: string } => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - days);
  return {
    startDate: start.toISOString().split("T")[0],
    endDate: end.toISOString().split("T")[0],
  };
};

const formatCurrency = (amount: number) =>
  amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const Analysis = () => {
  const defaultRange = getCurrentMonthRange();

  const [period, setPeriod] = useState<PeriodKey>("thisMonth");
  const [selectedShop, setSelectedShop] = useState<string>("all");
  const [startDate, setStartDate] = useState<string>(defaultRange.startDate);
  const [endDate, setEndDate] = useState<string>(defaultRange.endDate);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const [shopOptions, setShopOptions] = useState<ShopOption[]>([]);
  const [metrics, setMetrics] = useState<MetricPayload>(emptyMetrics);
  const [paymentMix, setPaymentMix] = useState<PaymentMixItem[]>([]);
  const [employeeSales, setEmployeeSales] = useState<EmployeeSalesItem[]>([]);
  const [customerSales, setCustomerSales] = useState<CustomerSalesItem[]>([]);
  const [shopSummary, setShopSummary] = useState<ShopSummaryItem[]>([]);

  useEffect(() => {
    if (period === "thisMonth") {
      const range = getCurrentMonthRange();
      setStartDate(range.startDate);
      setEndDate(range.endDate);
      return;
    }

    if (period === "last7Days") {
      const range = getRelativeRange(7);
      setStartDate(range.startDate);
      setEndDate(range.endDate);
      return;
    }

    if (period === "last30Days") {
      const range = getRelativeRange(30);
      setStartDate(range.startDate);
      setEndDate(range.endDate);
    }
  }, [period]);

  const loadAnalysis = useCallback(async () => {
    if (!startDate || !endDate) {
      setError("Select both start and end dates.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const params: {
        startDate: string;
        endDate: string;
        companyId?: number;
      } = { startDate, endDate };

      if (selectedShop !== "all") {
        params.companyId = Number(selectedShop);
      }

      const response = await companyApiService.getCompanyAnalysis(params);
      const payload: AnalysisResponse =
        (response?.data as AnalysisResponse) || (response as AnalysisResponse);

      setMetrics({
        ...emptyMetrics,
        ...(payload?.metrics || {}),
        totalStockSold: payload?.metrics?.totalStockSold ?? 0,
      });
      setPaymentMix(payload?.paymentMix || []);
      setEmployeeSales(payload?.employeeSales || []);
      setCustomerSales(payload?.customerSales || []);
      setShopSummary(payload?.shopSummary || []);
      setShopOptions(payload?.shopOptions || []);
    } catch (err) {
      console.error("Failed to load analysis:", err);
      setError("Failed to load analysis data.");
    } finally {
      setLoading(false);
    }
  }, [endDate, selectedShop, startDate]);

  useEffect(() => {
    loadAnalysis();
  }, [loadAnalysis]);

  const totalPaymentMix = useMemo(
    () => paymentMix.reduce((sum, item) => sum + item.value, 0),
    [paymentMix],
  );

  return (
    <div className="company-analysis-page">
      <div className="analysis-header">
        <div>
          <h1>Sales Analysis</h1>
          <p>
            Analyze sales, returns, purchasing, payment methods, and performance
            by shop.
          </p>
        </div>
      </div>

      <div className="analysis-filters">
        <div className="period-buttons">
          <button
            type="button"
            className={period === "thisMonth" ? "active" : ""}
            onClick={() => setPeriod("thisMonth")}
          >
            This Month
          </button>
          <button
            type="button"
            className={period === "last7Days" ? "active" : ""}
            onClick={() => setPeriod("last7Days")}
          >
            Last 7 Days
          </button>
          <button
            type="button"
            className={period === "last30Days" ? "active" : ""}
            onClick={() => setPeriod("last30Days")}
          >
            Last 30 Days
          </button>
          <button
            type="button"
            className={period === "custom" ? "active" : ""}
            onClick={() => setPeriod("custom")}
          >
            Custom
          </button>
        </div>

        <div className="filter-controls">
          <div className="control-field">
            <label htmlFor="analysis-shop">Shop</label>
            <select
              id="analysis-shop"
              value={selectedShop}
              onChange={(e) => setSelectedShop(e.target.value)}
            >
              <option value="all">All Shops</option>
              {shopOptions.map((shop) => (
                <option key={shop.id} value={shop.id}>
                  {shop.name}
                </option>
              ))}
            </select>
          </div>

          <div className="control-field">
            <label htmlFor="analysis-start">Start Date</label>
            <input
              id="analysis-start"
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPeriod("custom");
              }}
            />
          </div>

          <div className="control-field">
            <label htmlFor="analysis-end">End Date</label>
            <input
              id="analysis-end"
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPeriod("custom");
              }}
            />
          </div>

          <button type="button" className="apply-btn" onClick={loadAnalysis}>
            Apply
          </button>
        </div>
      </div>

      {error ? <div className="analysis-error">{error}</div> : null}

      {loading ? (
        <div className="analysis-loading">Loading analysis...</div>
      ) : (
        <>
          <div className="metric-grid">
            <div className="metric-card">
              <span>Total Revenue</span>
              <h3>{formatCurrency(metrics.totalRevenue)}</h3>
            </div>
            <div className="metric-card">
              <span>Total Sales</span>
              <h3>{formatCurrency(metrics.totalSales)}</h3>
            </div>
            <div className="metric-card">
              <span>Returned Amount</span>
              <h3>{formatCurrency(metrics.returnedAmount)}</h3>
            </div>
            <div className="metric-card">
              <span>Returned Items</span>
              <h3>{metrics.returnedItems.toLocaleString()}</h3>
            </div>
            <div className="metric-card">
              <span>Purchasing Total</span>
              <h3>{formatCurrency(metrics.purchasingTotal)}</h3>
            </div>
            <div className="metric-card">
              <span>Transactions</span>
              <h3>{metrics.totalTransactions.toLocaleString()}</h3>
            </div>
            <div className="metric-card">
              <span>Customers Added</span>
              <h3>{metrics.totalCustomers.toLocaleString()}</h3>
            </div>
            <div className="metric-card">
              <span>Employees Added</span>
              <h3>{metrics.totalEmployees.toLocaleString()}</h3>
            </div>
            <div className="metric-card">
              <span>Table Orders Sold</span>
              <h3>{formatQuantity(metrics.tableSales)}</h3>
            </div>
            <div className="metric-card">
              <span>Total Stock Sold</span>
              <h3>{formatQuantity(metrics.totalStockSold)}</h3>
            </div>
          </div>

          <div className="analysis-panels two-col">
            <div className="analysis-panel">
              <h2>Payment Method Mix</h2>
              <div className="payment-list">
                {paymentMix.map((item) => {
                  const pct =
                    totalPaymentMix > 0
                      ? (item.value / totalPaymentMix) * 100
                      : 0;
                  return (
                    <div className="payment-item" key={item.label}>
                      <div className="payment-row">
                        <span>{item.label}</span>
                        <strong>
                          {formatCurrency(item.value)} ({pct.toFixed(1)}%)
                        </strong>
                      </div>
                      <div className="payment-track">
                        <div
                          className="payment-fill"
                          style={{ width: `${Math.min(100, pct)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="analysis-panel">
              <h2>Credit Snapshot</h2>
              <div className="credit-grid">
                <div>
                  <span>Credit Sales</span>
                  <strong>{formatCurrency(metrics.creditSales)}</strong>
                </div>
                <div>
                  <span>Credit Recovered</span>
                  <strong>{formatCurrency(metrics.creditRecovered)}</strong>
                </div>
                <div>
                  <span>Card Sales</span>
                  <strong>{formatCurrency(metrics.cardSales)}</strong>
                </div>
                <div>
                  <span>Cash Sales</span>
                  <strong>{formatCurrency(metrics.cashSales)}</strong>
                </div>
                <div>
                  <span>Bank Sales</span>
                  <strong>{formatCurrency(metrics.bankSales)}</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="analysis-panel">
            <h2>Shop Sales Summary</h2>
            <div className="analysis-table-wrap">
              <table className="analysis-table">
                <thead>
                  <tr>
                    <th>Shop</th>
                    <th>Total Sales</th>
                    <th>Cash</th>
                    <th>Card</th>
                    <th>Bank</th>
                    <th>Credit</th>
                    <th>Transactions</th>
                  </tr>
                </thead>
                <tbody>
                  {shopSummary.length === 0 ? (
                    <tr>
                      <td colSpan={7}>No data for selected period.</td>
                    </tr>
                  ) : (
                    shopSummary.map((item) => (
                      <tr key={item.companyId}>
                        <td>{item.companyName}</td>
                        <td>{formatCurrency(item.totalSales)}</td>
                        <td>{formatCurrency(item.cashSales)}</td>
                        <td>{formatCurrency(item.cardSales)}</td>
                        <td>{formatCurrency(item.bankSales)}</td>
                        <td>{formatCurrency(item.creditSales)}</td>
                        <td>{item.transactions}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="analysis-panels two-col">
            <div className="analysis-panel">
              <h2>Employee Sales</h2>
              <div className="analysis-table-wrap">
                <table className="analysis-table compact">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Sales</th>
                      <th>Transactions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employeeSales.length === 0 ? (
                      <tr>
                        <td colSpan={3}>No employee sales in this period.</td>
                      </tr>
                    ) : (
                      employeeSales.map((item) => (
                        <tr key={item.employeeId}>
                          <td>{item.employeeName}</td>
                          <td>{formatCurrency(item.totalSales)}</td>
                          <td>{item.transactions}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="analysis-panel">
              <h2>Customer Sales</h2>
              <div className="analysis-table-wrap">
                <table className="analysis-table compact">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Phone</th>
                      <th>Sales</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerSales.length === 0 ? (
                      <tr>
                        <td colSpan={3}>No customer sales in this period.</td>
                      </tr>
                    ) : (
                      customerSales.map((item) => (
                        <tr key={item.customerId}>
                          <td>{item.customerName}</td>
                          <td>{item.phoneNumber || "-"}</td>
                          <td>{formatCurrency(item.totalSales)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Analysis;
